import type {
  Channel,
  InboxCategory,
  InboxStatus,
  ListInboxConversationsQuery,
} from '@sbc/domain-models/gateway';
import { CHANNELS, INBOX_CATEGORIES, INBOX_STATUSES } from '@sbc/domain-models/gateway';

import { pick } from '@/lib/utils';

export const ALL = 'all';
export const DEFAULT_STATUS: InboxStatus = 'needs_reply';
export const INBOX_PAGE_SIZE = 50;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const INBOX_TABS = ['all', 'unread', 'mine', 'unassigned', 'dms', 'comments'] as const;
export type InboxTab = (typeof INBOX_TABS)[number];

export const TAB_LABELS: Record<InboxTab, string> = {
  all: 'All',
  unread: 'Unread',
  mine: 'Mine',
  unassigned: 'Unassigned',
  dms: 'DMs',
  comments: 'Comments',
};

export interface InboxSearch {
  tab?: InboxTab;
  status?: InboxStatus | typeof ALL;
  channel?: Channel[];
  category?: InboxCategory;
  tag?: string;
  q?: string;
  page?: number;
}

export function parseInboxSearch(search: Record<string, unknown>): InboxSearch {
  const page = typeof search.page === 'number' ? search.page : Number(search.page);
  const tab = pick(INBOX_TABS, search.tab);
  return {
    tab: tab === 'all' ? undefined : tab,
    status: search.status === ALL ? ALL : pick(INBOX_STATUSES, search.status),
    channel: parseChannels(search.channel),
    category: pick(INBOX_CATEGORIES, search.category),
    tag: typeof search.tag === 'string' && UUID.test(search.tag) ? search.tag : undefined,
    q: typeof search.q === 'string' && search.q !== '' ? search.q : undefined,
    page: Number.isInteger(page) && page > 1 ? page : undefined,
  };
}

export function parseChannels(value: unknown): Channel[] | undefined {
  const values: unknown[] = Array.isArray(value) ? value : [value];
  const channels = CHANNELS.filter((channel) => values.includes(channel));
  return channels.length ? channels : undefined;
}

export function inboxQueryFor(search: InboxSearch): ListInboxConversationsQuery {
  const status = search.status ?? DEFAULT_STATUS;
  const tab = search.tab ?? 'all';
  return {
    page: search.page ?? 1,
    pageSize: INBOX_PAGE_SIZE,
    status: status === ALL ? undefined : status,
    channel: search.channel,
    category: search.category,
    tag: search.tag,
    search: search.q?.trim() || undefined,
    unread: tab === 'unread' ? true : undefined,
    assignee: tab === 'mine' ? 'me' : tab === 'unassigned' ? 'none' : undefined,
    surface: tab === 'dms' ? 'dm' : tab === 'comments' ? 'comment_thread' : undefined,
  };
}

export function withSearch(current: InboxSearch, patch: Partial<InboxSearch>): InboxSearch {
  const next: InboxSearch = { ...current, page: undefined, ...patch };
  if (next.status === DEFAULT_STATUS) {
    next.status = undefined;
  }
  if (next.tab === 'all') {
    next.tab = undefined;
  }
  if (!next.channel?.length) {
    next.channel = undefined;
  }
  return next;
}
